/**
 * Central registry of TanStack Query cache keys.
 *
 * Keeping them here (rather than inlined per-component) means a mutation,
 * an invalidation, or a WebSocket cache patch always agree on the exact
 * tuple - one source of truth per resource. Each helper returns a typed
 * tuple so consumers can pass it directly to `queryClient.setQueryData`,
 * `useQuery({ queryKey })`, and `queryClient.invalidateQueries`.
 */

/**
 * Hill list cache is keyed by lifecycle status so the home page (active) and
 * the archive page (done) keep independent caches. A WS event that mutates
 * one list (`hill:completed` removes from active, adds to done) invalidates
 * the shared `hillsListPrefix` so both keys refetch.
 */
export type HillListStatus = 'active' | 'done' | 'all';

export const queryKeys = {
  session: () => ['session', 'me'] as const,
  gitlabProjects: () => ['gitlab', 'projects'] as const,
  /**
   * Shared prefix used for invalidation when a hill transitions between
   * active and done lists. TanStack Query matches by prefix, so passing
   * this key to `invalidateQueries` clears every status-keyed list at once.
   */
  hillsListPrefix: () => ['hills', 'list'] as const,
  hillsList: (status: HillListStatus = 'active') => ['hills', 'list', status] as const,
  hill: (hillId: string) => ['hills', 'detail', hillId] as const,
  snapshots: (hillId: string) => ['hills', 'snapshots', hillId] as const,
};
